'use client';

import React, { useState } from 'react';
import cn from 'classnames';

import { responsiveBreakpointsPx } from '@/constants';
import Slider from '@/components/molecules/Slider/Slider';
import { Game } from '@/packages/sxm/sports/games/GamesResponse';
import { Race } from '@/packages/sxm/sports/races';
import { Event } from '@/packages/sxm/sports/events';
import { LeagueGameEntry } from '@/services/contentful/contentTypes/UpcomingSports';
import { Text } from '@/ui-components/atoms/Text/Text';
import { GameCard } from '@/ui-components/molecules/GameCard/GameCard';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import {
    GameCardSliderFourOptions,
    GameCardSliderFourPlusOptions,
    GameCardSliderOneOption,
    GameCardSliderThreeOptions,
    GameCardSliderTwoOptions,
} from '@/components/molecules/Slider/SliderOptions';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import ImageOptimization from '@/services/image_optimization';
import type { Options as SplideOptions } from '@splidejs/splide';

import styles from './gameCard.module.scss';

type GameRaceEvent = Game | Race | Event;

interface Slide {
    content: React.ReactNode;
    link?: Link;
}

interface Props {
    asset: SiteUpcomingSports;
    inMixtape?: boolean;
}

const prevArrowClassNames = cn('splide__arrow splide__arrow--prev', styles.leftArrow);
const nextArrowClassNames = cn('splide__arrow splide__arrow--next', styles.rightArrow);

const arrows = (aria: string) => (
    <div className={`splide__arrows ${styles.carouselArrows}`}>
        <button className={prevArrowClassNames} aria-label={`${aria} navigate previous arrow`}>
            <ChevronIcon />
        </button>
        <button className={nextArrowClassNames} aria-label={`${aria} navigate next arrow`}>
            <ChevronIcon />
        </button>
    </div>
);

const GameCards = ({ asset, inMixtape }: Props) => {
    const teamData = asset.fields.league_games.team;
    const nonTeamData = asset.fields.league_games.nonTeam;
    const teams = asset.fields.teams;
    const leagues = asset.fields.leagues;
    const [now] = useState(() => Date.now());

    const hasCopyright =
        teamData.some((leagueEntry: LeagueGameEntry) =>
            leagueEntry.games.some((game: GameRaceEvent) => {
                if ('awayTeam' in game && 'homeTeam' in game) {
                    const leagueData = leagues[game.homeLeagueAbbreviation ?? ''];
                    const awayTeamData = teams[leagueData.abbreviation + game.awayTeamAbbreviation];
                    const homeTeamData = teams[leagueData.abbreviation + game.homeTeamAbbreviation];
                    return awayTeamData?.logo?.copyrightText || homeTeamData?.logo?.copyrightText;
                } else if ('eventName' in game) {
                    const eventTeamData = leagues[game.homeLeagueAbbreviation ?? ''];
                    return eventTeamData?.logo?.copyrightText;
                } else if ('raceOrDriverName' in game) {
                    const eventTeamData = leagues[game.seriesKey ?? ''];
                    return eventTeamData?.logo?.copyrightText;
                }
                return false;
            }),
        ) ||
        nonTeamData.some((leagueEntry: LeagueGameEntry) =>
            leagueEntry.games.some((game: GameRaceEvent) => {
                if ('awayTeam' in game && 'homeTeam' in game) {
                    const leagueData = leagues[game.homeLeagueAbbreviation ?? ''];
                    const awayTeamData = teams[leagueData.abbreviation + game.awayTeamAbbreviation];
                    const homeTeamData = teams[leagueData.abbreviation + game.homeTeamAbbreviation];
                    return awayTeamData?.logo?.copyrightText || homeTeamData?.logo?.copyrightText;
                } else if ('eventName' in game) {
                    const eventTeamData = leagues[game.homeLeagueAbbreviation ?? ''];
                    return eventTeamData?.logo?.copyrightText;
                } else if ('raceOrDriverName' in game) {
                    const eventTeamData = leagues[game.seriesKey ?? ''];
                    return eventTeamData?.logo?.copyrightText;
                }
                return false;
            }),
        );

    const renderSportsCards = (data: LeagueGameEntry[]) => {
        return data.flatMap((leagueEntry) => {
            const activeGames = leagueEntry.games
                .filter((game: GameRaceEvent) => {
                    const endDate =
                        ('endDateTime' in game && game.endDateTime) || ('gameEndDate' in game && game.gameEndDate);
                    if (!endDate) return true;
                    const endTs = Date.parse(endDate);
                    return Number.isNaN(endTs) || endTs > now;
                })
                .slice(0, leagueEntry.displayCount);

            return activeGames
                .map((game: GameRaceEvent, index: number) => {
                    if ('awayTeam' in game && 'homeTeam' in game) {
                        const leagueData = leagues[game.homeLeagueAbbreviation ?? ''];
                        const leagueTitle = leagueData.isSoccer ? 'SOCCER' : (leagueData.title ?? '');

                        const awayTeamData = teams[leagueData.abbreviation + (game.awayTeamAbbreviation ?? '')];
                        const homeTeamData = teams[leagueData.abbreviation + (game.homeTeamAbbreviation ?? '')];

                        const awayTeamName = awayTeamData?.teamName;
                        const homeTeamName = homeTeamData?.teamName;

                        const awayLogoUrl =
                            (awayTeamData?.logo?.url &&
                                ImageOptimization({ src: awayTeamData?.logo?.url, width: 150, height: 77 })) ??
                            '';
                        const awayLogoCopyright = hasCopyright ? awayTeamData?.logo?.copyrightText || ' ' : undefined;
                        const homeLogoUrl =
                            (homeTeamData?.logo?.url &&
                                ImageOptimization({ src: homeTeamData?.logo?.url, width: 150, height: 77 })) ??
                            '';
                        const homeLogoCopyright = hasCopyright ? homeTeamData?.logo?.copyrightText || ' ' : undefined;
                        const leaguePageUrl = leagueData?.leaguePageUrl ?? '#';
                        const awayTeamPageUrl = awayTeamData?.pageUrl ? awayTeamData?.pageUrl : leaguePageUrl;
                        const homeTeamPageUrl = homeTeamData?.pageUrl ? homeTeamData?.pageUrl : leaguePageUrl;

                        let awayChannelNumber;
                        let awayOnline = false;
                        let homeChannelNumber;
                        let homeOnline = false;
                        if (game.awayStream) {
                            awayChannelNumber = game.awayStream;
                        } else if (game.nationalStream) {
                            awayChannelNumber = game.nationalStream;
                        } else if (game.awayInternetStream || game.nationalInternetStream) {
                            awayOnline = true;
                        }
                        if (game.homeStream) {
                            homeChannelNumber = game.homeStream;
                        } else if (game.nationalStream) {
                            homeChannelNumber = game.nationalStream;
                        } else if (game.homeInternetStream || game.nationalInternetStream) {
                            homeOnline = true;
                        }

                        const sportsCardBlock = (
                            <div key={index} className={styles.sportsCard}>
                                <GameCard
                                    dateTime={game.gameDate ? new Date(game.gameDate) : new Date()}
                                    teams={[
                                        {
                                            title: awayTeamName ?? '',
                                            subtitle: 'away',
                                            channel: awayChannelNumber?.toString(),
                                            isOnline: awayOnline,
                                            link: {
                                                href: awayTeamPageUrl,
                                                ['aria-label']: game.awayTeam || '',
                                            },
                                            image: {
                                                src: awayLogoUrl,
                                                alt: game.awayTeam ?? '',
                                            },
                                            backgroundColor: awayTeamData?.color || '',
                                            nationalChannel: game.nationalStream?.toString(),
                                            spanishChannel: game.spanishStream?.toString(),
                                            channelStream: game.awayInternetStream?.toString(),
                                            nationalStream: game.nationalInternetStream?.toString(),
                                            spanishStream: game.spanishInternetStream?.toString(),
                                            channelStreamLink: game.awayStreamLink?.toString(),
                                            nationalStreamLink: game.nationalStreamLink?.toString(),
                                            spanishStreamLink: game.spanishStreamLink?.toString(),
                                        },
                                        {
                                            title: homeTeamName ?? '',
                                            subtitle: 'home',
                                            channel: homeChannelNumber?.toString(),
                                            isOnline: homeOnline,
                                            link: {
                                                href: homeTeamPageUrl,
                                                ['aria-label']: game.homeTeam || '',
                                            },
                                            image: {
                                                src: homeLogoUrl,
                                                alt: game.homeTeam ?? '',
                                            },
                                            backgroundColor: homeTeamData?.color || '',
                                            nationalChannel: game.nationalStream?.toString(),
                                            spanishChannel: game.spanishStream?.toString(),
                                            channelStream: game.homeInternetStream?.toString(),
                                            nationalStream: game.nationalInternetStream?.toString(),
                                            spanishStream: game.spanishInternetStream?.toString(),
                                            channelStreamLink: game.homeStreamLink?.toString(),
                                            nationalStreamLink: game.nationalStreamLink?.toString(),
                                            spanishStreamLink: game.spanishStreamLink?.toString(),
                                        },
                                    ]}
                                    link={{
                                        href: leaguePageUrl,
                                    }}
                                    linkText={`${leagueTitle} schedule`}
                                    firstCopyright={awayLogoCopyright}
                                    secondCopyright={homeLogoCopyright}
                                />
                            </div>
                        );

                        return {
                            content: sportsCardBlock,
                        };
                    } else if ('eventName' in game) {
                        const eventTeamData = leagues[game.homeLeagueAbbreviation ?? ''];
                        const eventTitle = eventTeamData?.title ?? '';
                        const eventLogoUrl =
                            (eventTeamData?.logo?.url &&
                                ImageOptimization({ src: eventTeamData?.logo?.url, width: 150, height: 72 })) ??
                            '';
                        const eventLogoCopyright = hasCopyright ? eventTeamData?.logo?.copyrightText || ' ' : undefined;
                        const leaguePageUrl = eventTeamData?.leaguePageUrl ? eventTeamData?.leaguePageUrl : '#';

                        let eventChannelNumber;
                        let eventOnline = false;
                        if (game.nationalStream) {
                            eventChannelNumber = game.nationalStream;
                        } else if (game.nationalInternetStream) {
                            eventOnline = true;
                        }

                        const sportsCardBlock = (
                            <div key={index} className={styles.sportsCard}>
                                <GameCard
                                    dateTime={game.gameDate ? new Date(game.gameDate) : new Date()}
                                    teams={[
                                        {
                                            title: game.eventName ?? '',
                                            subtitle: game.venueName || '',
                                            channel: eventChannelNumber?.toString(),
                                            isOnline: eventOnline,
                                            link: {
                                                href: leaguePageUrl,
                                                ['aria-label']: eventTitle || '',
                                            },
                                            image: {
                                                src: eventLogoUrl,
                                                alt: 'race',
                                            },
                                            backgroundColor: eventTeamData?.color || '#000000',
                                            nationalChannel: game.nationalStream?.toString(),
                                            spanishChannel: game.spanishStream?.toString(),
                                            nationalStream: game.nationalInternetStream?.toString(),
                                            spanishStream: game.spanishInternetStream?.toString(),
                                            nationalStreamLink: game.nationalStreamLink?.toString(),
                                            spanishStreamLink: game.spanishStreamLink?.toString(),
                                        },
                                    ]}
                                    link={{
                                        href: leaguePageUrl,
                                    }}
                                    linkText={`${eventTitle} schedule`}
                                    firstCopyright={eventLogoCopyright}
                                    // In case content author doesn't want this overriding (We may revisit if frequent):
                                    useContrastingImageColors={eventLogoUrl.endsWith('.svg')}
                                />
                            </div>
                        );

                        return {
                            content: sportsCardBlock,
                        };
                    } else if ('raceOrDriverName' in game) {
                        const eventTeamData = leagues[game.seriesKey ?? ''];
                        const eventTitle = 'NASCAR';
                        const eventLogoUrl =
                            (eventTeamData?.logo?.url &&
                                ImageOptimization({ src: eventTeamData?.logo?.url, width: 150, height: 72 })) ??
                            '';
                        const eventLogoCopyright = hasCopyright ? eventTeamData?.logo?.copyrightText || ' ' : undefined;
                        const leaguePageUrl = eventTeamData?.leaguePageUrl ? eventTeamData?.leaguePageUrl : '#';

                        let raceChannelNumber;
                        let raceOnline = false;
                        if (game.nationalStream) {
                            raceChannelNumber = game.nationalStream;
                        } else if (game.nationalInternetStream) {
                            raceOnline = true;
                        }

                        const sportsCardBlock = (
                            <div key={index} className={styles.sportsCard}>
                                <GameCard
                                    dateTime={game.startDateTime ? new Date(game.startDateTime) : new Date()}
                                    teams={[
                                        {
                                            title: game.raceOrDriverName ?? '',
                                            subtitle: game.venueOrTeamName || '',
                                            channel: raceChannelNumber?.toString(),
                                            isOnline: raceOnline,
                                            link: {
                                                href: leaguePageUrl,
                                                ['aria-label']: eventTitle || '',
                                            },
                                            image: {
                                                src: eventLogoUrl,
                                                alt: 'race',
                                            },
                                            backgroundColor: eventTeamData?.color || '#000000',
                                            nationalChannel: game.nationalStream?.toString(),
                                            spanishChannel: game.spanishStream?.toString(),
                                            nationalStream: game.nationalInternetStream?.toString(),
                                            spanishStream: game.spanishInternetStream?.toString(),
                                            nationalStreamLink: game.nationalStreamLink?.toString(),
                                            spanishStreamLink: game.spanishStreamLink?.toString(),
                                        },
                                    ]}
                                    link={{
                                        href: leaguePageUrl,
                                    }}
                                    linkText={`${eventTitle} schedule`}
                                    firstCopyright={eventLogoCopyright}
                                />
                            </div>
                        );

                        return {
                            content: sportsCardBlock,
                        };
                    }
                })
                .filter((slide) => slide !== null) as Slide[];
        });
    };

    const teamCards = renderSportsCards(teamData);
    const nonTeamCards = renderSportsCards(nonTeamData);

    const getSlider = (options: React.ComponentProps<typeof Slider>['splideOptions'], cards: Slide[], aria: string) => {
        const paddingConsiderateOptions: SplideOptions = inMixtape
            ? {
                  ...options,
                  padding: '16px',
                  breakpoints: {
                      ...options.breakpoints,
                      [responsiveBreakpointsPx.sm]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.sm] || {}),
                          padding: '16px',
                      },
                      [responsiveBreakpointsPx.md]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.md] || {}),
                          padding: '24px',
                      },
                      [responsiveBreakpointsPx.lg]: {
                          ...(options.breakpoints?.[responsiveBreakpointsPx.lg] || {}),
                          padding: cards?.length === 2 ? '25%' : '0',
                      },
                  },
              }
            : options;

        return (
            <Slider
                slides={cards}
                isThemeDark={false}
                splideOptions={{ ...paddingConsiderateOptions, label: aria }}
                type="Default"
                customArrows={cards?.length > 4 ? arrows(aria) : undefined}
                className={styles.slider}
            />
        );
    };

    const getSliderByNumber = (length: number, cards: Slide[], aria: string) => {
        switch (length) {
            case 1:
                return getSlider(GameCardSliderOneOption, cards, aria);
            case 2:
                return getSlider(GameCardSliderTwoOptions, cards, aria);
            case 3:
                return getSlider(GameCardSliderThreeOptions, cards, aria);
            case 4:
                return getSlider(GameCardSliderFourOptions, cards, aria);
            default:
                return getSlider(GameCardSliderFourPlusOptions, cards, aria);
        }
    };

    const getWrapperClasses = (length: number) =>
        cn(styles.title, {
            [styles.centerTitle]: length < 4,
        });

    return (
        <Grid container>
            {teamCards?.length && teamCards.length > 0 ? (
                <Grid className={styles.wrapperFirstRow}>
                    {asset.fields.team_games_headine && (
                        <Text tag="h5" className={getWrapperClasses(teamCards.length)}>
                            {asset.fields.team_games_headine}
                        </Text>
                    )}
                    {getSliderByNumber(teamCards.length, teamCards, 'Team Sports Carousel')}
                </Grid>
            ) : undefined}

            {nonTeamCards?.length && nonTeamCards.length > 0 ? (
                <Grid className={styles.wrapperSecondRow}>
                    {asset.fields.nonteam_games_headline && (
                        <Text tag="h5" className={getWrapperClasses(nonTeamCards.length)}>
                            {asset.fields.nonteam_games_headline}
                        </Text>
                    )}
                    {getSliderByNumber(nonTeamCards.length, nonTeamCards, 'Event Carousel')}
                </Grid>
            ) : undefined}
        </Grid>
    );
};

export default GameCards;
